import { constants, selectors } from 'modules/common';
import { isSortCodeValid } from 'shared/helpers';
import { z } from 'zod';

import { isValidXeroContactName } from '../../../selectors/xero';
import { messages as contactInformationMessages } from './components/ContactInformationSection/ContactInformationSection.messages';
import { messages } from './XeroCreateContactPopup.messages';

const phoneSchema = z.object({
    countryCode: z.string().refine(selectors.ui.isValidPhone).optional(),
    areaCode: z.string().refine(selectors.ui.isValidPhone).optional(),
    number: z.string().refine(selectors.ui.isValidPhone).optional(),
});

export const schema = z
    .object({
        name: z
            .string()
            .trim()
            .min(1, messages.requiredField({ field: contactInformationMessages.nameLabel }))
            .max(constants.xeroConstants.CONTACT_NAME_MAX_LENGTH)
            .refine(isValidXeroContactName, messages.invalidContactName({})),
        accountNumber: z.string().trim().max(constants.xeroConstants.ACCOUNT_NUMBER_MAX_LENGTH).optional(),
        firstName: z.string().trim().max(constants.xeroConstants.FIRST_NAME_MAX_LENGTH).optional(),
        lastName: z.string().trim().max(constants.xeroConstants.LAST_NAME_MAX_LENGTH).optional(),
        email: z.union([
            z.literal(''),
            z.string().trim().max(constants.xeroConstants.EMAIL_ADDRESS_MAX_LENGTH).email().optional(),
        ]),
        website: z
            .string()
            .optional()
            .refine((value) => (value ? selectors.ui.isValidWebsite(value) : true), messages.invalidWebsite),
        phone: phoneSchema,
        fax: phoneSchema,
        mobile: phoneSchema,
        directDial: phoneSchema,
        bankAccount: z
            .object({
                sortCode: z.string().max(constants.xeroConstants.sortCodeMaxLength).optional(),
                bankAccountDetails: z.string().max(constants.xeroConstants.BANK_ACCOUNT_DETAILS_MAX_LENGTH).optional(),
            })
            .optional()
            .superRefine((value, context) => {
                if (!value?.sortCode) {
                    return z.NEVER;
                }

                if (!isSortCodeValid(value.sortCode)) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: messages.invalidSortCode,
                        path: ['sortCode'],
                    });
                }

                if (!/^\d{8}$/.test(value.bankAccountDetails ?? '')) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: messages.invalidBankAccountDetails,
                        path: ['bankAccountDetails'],
                    });
                }
            }),
    })
    .passthrough();
